import { z } from "zod";
import { PLATFORMS } from "./db.js";
import { JOB_STATUSES, JOB_TYPES } from "./jobs.js";
import { stripUrlParams } from "./video-url.js";

// ─── Auth ───────────────────────────────────────────────────
export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export type MeResponse = {
  user: { id: number; email: string; display_name: string | null; role: "admin" | "viewer" };
  csrfToken: string;
};

// ─── Project CRUD ───────────────────────────────────────────
export const ProjectPageInputSchema = z.object({
  id: z.number().int().optional(),
  koc_id: z.number().int().positive().nullable().optional(),
  platform: z.enum(PLATFORMS),
  profile_url: z.string().url().transform(stripUrlParams),
  label: z.string().max(60).nullable().optional(),
  enabled: z.boolean(),
});
export type ProjectPageInput = z.infer<typeof ProjectPageInputSchema>;

export const ProjectPayloadSchema = z.object({
  account_name: z.string().min(1).max(120),
  notes: z.string().max(2000).nullable().optional(),
  monitor_keywords: z.array(z.string().min(1)).max(50),
  enabled: z.boolean(),
  pages: z.array(ProjectPageInputSchema).max(50),
});
export type ProjectPayload = z.infer<typeof ProjectPayloadSchema>;

export type ProjectPageSummary = {
  id: number;
  koc_id: number | null;
  koc_name: string | null;
  platform: "tiktok" | "instagram" | "youtube";
  profile_url: string;
  label: string | null;
  enabled: boolean;
};

export type ProjectListItem = {
  id: number;
  account_name: string;
  notes: string | null;
  monitor_keywords: string[];
  enabled: boolean;
  created_at: number;
  updated_at: number;
  video_count: number;
  hit_count: number;
  pages: ProjectPageSummary[];
};

export type ProjectDetail = {
  id: number;
  account_name: string;
  notes: string | null;
  monitor_keywords: string[];
  enabled: boolean;
  created_at: number;
  updated_at: number;
  pages: ProjectPageSummary[];
};

// ─── KOC CRUD ───────────────────────────────────────────────
export const KocPayloadSchema = z.object({
  name: z.string().min(1).max(120),
  notes: z.string().max(2000).nullable().optional(),
});
export type KocPayload = z.infer<typeof KocPayloadSchema>;

export type KocListItem = {
  id: number;
  name: string;
  notes: string | null;
  created_at: number;
  updated_at: number;
  video_count: number;
  project_count: number;
};

export type KocPageRef = {
  page_id: number;
  project_id: number;
  project_name: string;
  platform: "tiktok" | "instagram" | "youtube";
  profile_url: string;
  label: string | null;
};

export type KocDetail = {
  id: number;
  name: string;
  notes: string | null;
  created_at: number;
  updated_at: number;
  pages: KocPageRef[];
};

// ─── Videos ─────────────────────────────────────────────────
export const VideoListQuerySchema = z.object({
  projectId: z.coerce.number().int().optional(),
  kocId: z.coerce.number().int().optional(),
  platform: z.enum(PLATFORMS).optional(),
  title: z.string().optional(),
  keyword: z.string().optional(),
  hasHit: z.enum(["1", "0"]).optional(),
  publishedFrom: z.coerce.number().int().optional(),
  publishedTo: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type VideoListQuery = z.infer<typeof VideoListQuerySchema>;

export type VideoListItem = {
  id: number;
  project_id: number;
  project_name: string;
  koc_id: number | null;
  koc_name: string | null;
  page_id: number | null;
  page_label: string | null;
  platform: "tiktok" | "instagram" | "youtube";
  url: string;
  title: string | null;
  publish_time: number;
  has_keyword_hit: boolean;
  keyword_hits: string[];
  latest_view_count: number | null;
  latest_like_count: number | null;
  latest_comment_count: number | null;
};

export type VideoDetail = VideoListItem & {
  description: string | null;
  thumbnail_url: string | null;
  duration_seconds: number | null;
  first_seen_at: number;
  last_scraped_at: number;
  snapshots: Array<{
    snapshot_type: "daily" | "24h" | "7d";
    captured_at: number;
    hours_since_publish: number;
    view_count: number | null;
    like_count: number | null;
    comment_count: number | null;
    share_count: number | null;
    save_count: number | null;
  }>;
};

// ─── Manual video import ────────────────────────────────────
// One row in a create-or-import call.
//   * `project_id` references an existing project (no auto-create — keyword matching depends
//     on the project's monitor_keywords, so the user must configure them deliberately first).
//   * Exactly one of `koc_id` / `koc_name` must be supplied. `koc_id` picks an existing KOC;
//     `koc_name` auto-creates a new KOC with that name on submit.
//   * `url` is the video URL; the server derives platform_video_id from it and rejects rows
//     where the URL doesn't parse.
//   * `page_label` optionally identifies *which* page on (project, platform) this video
//     belongs to (a project can have multiple pages on one platform, e.g. Main vs Brand).
//     Server resolution mirrors the previous `account_label` flow:
//       - label provided + existing page matches → use that page (and refresh koc_id if it's null)
//       - label provided + no match → create a new page with this label, koc_id from the row
//       - label omitted / blank → use the project's first existing page for the platform,
//                                 or create one with NULL label if none exists
export const VideoImportItemSchema = z.object({
  project_id: z.number().int().positive(),
  koc_id: z.number().int().positive().optional(),
  koc_name: z.string().min(1).max(120).optional(),
  platform: z.enum(PLATFORMS),
  url: z.string().url().max(2000).transform(stripUrlParams),
  page_label: z.string().max(60).nullable().optional(),
}).refine((d) => Number(!!d.koc_id) + Number(!!d.koc_name) === 1, {
  message: "exactly one of koc_id / koc_name required",
});
export type VideoImportItem = z.infer<typeof VideoImportItemSchema>;

export const VideoImportPayloadSchema = z.object({
  videos: z.array(VideoImportItemSchema).min(1).max(500),
});
export type VideoImportPayload = z.infer<typeof VideoImportPayloadSchema>;

// Per-row result. `status` is `inserted` for newly-added videos (a manual_video_initial
// job is enqueued, expect title/publish_time to populate within minutes), `duplicate`
// when (platform, platform_video_id) is already in the db, or `error` for rejected rows
// (e.g. unparseable URL, unknown project_id).
export type VideoImportRowResult = {
  project_id: number;
  project_name: string | null;
  koc_id: number | null;
  koc_name: string | null;
  platform: "tiktok" | "instagram" | "youtube";
  page_label: string | null;
  url: string;
  status: "inserted" | "duplicate" | "error";
  video_id?: number;
  page_id?: number;
  scheduled_job_id?: number;
  error?: string;
  // On `duplicate`, the project/koc the existing video row already belongs to.
  // May differ from the upload's target project_id/koc_id — that's the whole point
  // of surfacing it (the operator usually wants to know who imported it first).
  existing_project_id?: number | null;
  existing_project_name?: string | null;
  existing_koc_id?: number | null;
  existing_koc_name?: string | null;
};

export type VideoImportResponse = {
  inserted: number;
  duplicates: number;
  errors: number;
  results: VideoImportRowResult[];
};

// ─── Jobs board ─────────────────────────────────────────────
export const JobListQuerySchema = z.object({
  status: z.enum(JOB_STATUSES).optional(),
  jobType: z.enum(JOB_TYPES).optional(),
  projectId: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type JobListQuery = z.infer<typeof JobListQuerySchema>;

export type JobListItem = {
  id: number;
  job_type: (typeof JOB_TYPES)[number];
  status: (typeof JOB_STATUSES)[number];
  project_id: number | null;
  project_name: string | null;
  page_id: number | null;
  page_label: string | null;
  video_id: number | null;
  platform: string | null;
  due_at: number;
  attempts: number;
  last_error: string | null;
  created_at: number;
  dispatched_at: number | null;
  completed_at: number | null;
};

// ─── Stats ──────────────────────────────────────────────────
export type StatsOverview = {
  project_count: number;
  enabled_project_count: number;
  koc_count: number;
  video_count: number;
  videos_last_7d: number;
  hits_last_30d: Array<{ date: string; count: number }>;
};

export type StatsKeywords = {
  keywords: Array<{ keyword: string; hit_count: number }>;
};
